/**
 * Pure booking helpers, extracted from ZuzuRedesign so they can be unit
 * tested independently of Supabase and the UI. No side effects.
 */

export type BookingMode = "single" | "session" | "room";

/** Design modality id → backend machine_id. */
export const MACHINE_MAP: Record<string, string> = {
  plunge: "plunge",
  sauna: "sauna",
  comp: "compression",
  red: "redlight",
  inbody: "inbody",
  shower: "shower",
};

/** backend machine_id → member-facing name. */
export function machineLabel(id: string): string {
  return (
    {
      plunge: "cold plunge",
      "plunge-1": "cold plunge · 7°",
      "plunge-2": "cold plunge · 12°",
      sauna: "nordic sauna",
      compression: "compression",
      redlight: "red light",
      inbody: "inbody scan",
      shower: "rinse shower",
    }[id] || id
  );
}

/**
 * A station's name at pool level: the two plunge tubs both read "cold plunge",
 * without the temperature suffix. Used when stations are listed together —
 * machineLabel's "cold plunge · 7°" collides with the " · " that joins them.
 */
export function stationLabel(id: string | null | undefined): string {
  const key = String(id || "").replace(/-\d+$/, "");
  // Never an empty string: this lands in a table cell and a blank one reads as
  // a rendering fault rather than as missing data.
  return key ? machineLabel(key) : "session";
}

/**
 * What to call a booking made as one purchase.
 *
 * A stack is stored as one row per station, so both the upcoming list and the
 * visit history collapse it to a single entry and need one name for it. A
 * short stack reads better as its stations; a long one doesn't fit the history
 * table's column and wraps, so it gets a count instead.
 *
 * The rinse shower is left out of both: it's added automatically alongside a
 * sauna or plunge, so a member who chose five stations should not be told they
 * booked six.
 */
export function stackLabel(machineIds: (string | null | undefined)[]): string {
  const chosen = machineIds.filter(
    (id) => id && !isShowerStep(String(id)),
  ) as string[];
  if (chosen.length === 0) return "recovery session";
  if (chosen.length === 1) return stationLabel(chosen[0]);
  if (chosen.length <= 3) return chosen.map(stationLabel).join(" · ");
  return `recovery session · ${chosen.length} stations`;
}

/** A rinse-off shower is always 3 minutes. */
export const SHOWER_MINUTES = 3;

/**
 * True if a stored booking's machine_id belongs to `poolId`.
 *
 * Most machines are one unit and match by name. The cold plunge is not: the
 * booking RPCs never store 'plunge', they pick a concrete tub and store
 * 'plunge-1' or 'plunge-2'. Anything comparing a booking to the pool id with
 * plain equality therefore misses every plunge booking ever made — which is
 * what emptied the cold plunge column on the door display, and before that
 * made get_live_capacity report both tubs free while both were occupied
 * (see migration 20260906140000).
 */
export function machineInPool(
  machineId: string | null | undefined,
  poolId: string,
): boolean {
  if (!machineId) return false;
  return machineId === poolId || machineId.startsWith(`${poolId}-`);
}

/** True if a step is a cold plunge (the pool, a specific tub, or the design id). */
export function isPlungeStep(id: string): boolean {
  const m = MACHINE_MAP[id] || id;
  return id === "plunge" || m === "plunge" || m.startsWith("plunge");
}

/** True if a step is the sauna. */
export function isSaunaStep(id: string): boolean {
  return id === "sauna" || (MACHINE_MAP[id] || id) === "sauna";
}

/** True if a step is the shower. */
export function isShowerStep(id: string): boolean {
  return id === "shower" || (MACHINE_MAP[id] || id) === "shower";
}

/**
 * Cancellation tiers, as a share of what the member actually PAID (see
 * _zz_group_paid_credits): 100% back more than 48 hours before the start, 50%
 * more than 12, nothing inside 12. Mirrors _zz_refund_pct.
 */
/**
 * Changing time: minutes a member may stay past their time before the overstay
 * meter starts.
 *
 * Five minutes was the old grace, and five minutes is not enough to get out of
 * a cold plunge, rinse, dry and dress — so the meter was charging people for
 * getting changed. It used to scale with the booking (10 minutes under half an
 * hour, 20 over) and it no longer does: one number is one thing to remember,
 * and a member standing in a changing room should not have to work out which
 * tier they are on. In from five minutes before, ten minutes after. Every
 * booking, long or short.
 *
 * This is time in the BUILDING, not on the equipment. A machine is still
 * released at its booked end, since the member getting changed is not standing
 * on it, so nothing here books anything out for longer than it was booked.
 *
 * Mirrors _zz_leeway_minutes in
 * 20260913160000_paid_time_and_one_flat_leeway.sql; a test reads the number
 * back out of that migration.
 */
export const LEEWAY_MIN = 10;

/** Changing time. Flat, so the argument is ignored — see LEEWAY_MIN. */
export function leewayMinutesFor(_bookedMinutes?: number): number {
  return LEEWAY_MIN;
}

/* ── paid time ────────────────────────────────────────────────────────────── */

/**
 * What a credit buys, in minutes. A booking is priced on these and rounded UP
 * to whole credits, so a 10-minute plunge off-peak costs the same 1 credit as a
 * 30-minute one — the member has paid for 30 minutes and booked 10.
 *
 * Until now nothing told them that, and the overstay meter didn't know it
 * either: it started ten minutes after the booked END, so the twenty minutes
 * they had already paid for were billed to them a second time. Paid time is the
 * time their credits bought, and it is the clock everything below runs on.
 *
 * Mirrors _zz_credit_units (30 min/credit off-peak, 15 peak) and the
 * GREATEST(1, CEIL(...)) its callers apply.
 */
export const MIN_PER_CREDIT_OFF_PEAK = 30;
export const MIN_PER_CREDIT_PEAK = 15;

/**
 * Off-peak bounds (minutes from midnight, SAST) for a YYYY-MM-DD date.
 * Mirrors _zz_off_peak_bounds and ZZ_OFF_PEAK_BOUNDS in zzHelpers.js:
 * Sun fully off-peak, Sat off-peak from 13:00, Mon–Fri 09:00–16:00.
 */
export function offPeakBoundsFor(date: string): { start: number; end: number } {
  // Noon anchors the date against any timezone shift in the runtime — a
  // midnight Date can land on the previous day and pick the wrong schedule.
  const dow = new Date(`${date}T12:00:00`).getDay();
  if (dow === 0) return { start: 6 * 60, end: 20 * 60 };
  if (dow === 6) return { start: 13 * 60, end: 20 * 60 };
  return { start: 9 * 60, end: 16 * 60 };
}

/**
 * Minutes the member's credits actually bought, given what they booked.
 *
 * The rounding-up leftover is converted back into minutes at the rate of the
 * window their booking ENDS in, since that is where the extra time runs on
 * from. A 10-minute plunge off-peak: a third of a credit, charged one, two
 * thirds left over × 30 minutes = 20 extra — so 30 minutes of paid time.
 *
 * A private room is priced differently (a flat tier under half an hour, then by
 * the hour) so its block is worked out from those, not from credit units.
 *
 * Returns bookedMinutes unchanged when it can't price the booking safely — no
 * date, or a session with a gap in it, whose real charge is summed per station.
 * Under-promising is the only acceptable direction to be wrong in here.
 */
export function paidBlockFor(v: {
  date?: string | null;
  start: string;
  end: string;
  bookedMinutes: number;
  kind?: "room" | "session";
}): { minutes: number; credits: number | null } {
  const booked = Math.max(0, Math.round(v.bookedMinutes || 0));
  const asBooked = { minutes: booked, credits: null };
  if (!v.date || booked <= 0) return asBooked;
  const startMin = timeToMinutes(hhmmOf(v.start));
  const endMin = timeToMinutes(hhmmOf(v.end));
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return asBooked;

  const { start: offStart, end: offEnd } = offPeakBoundsFor(v.date);
  const spanEnd = startMin + booked;

  if (v.kind === "room") {
    const offMin = Math.max(
      0,
      Math.min(spanEnd, offEnd) - Math.max(startMin, offStart),
    );
    const peakMin = booked - offMin;
    // A straddling room booking is billed pro rata by the minute — nothing is
    // rounded up into a block, so there is no spare time to hand back.
    if (offMin > 0 && peakMin > 0) return asBooked;
    // The flat half-hour tier, then whole hours. Credits aren't named for a
    // room: it is priced by the hour, not in 30-minute credits.
    if (booked <= 30) return { minutes: 30, credits: null };
    return { minutes: Math.ceil(booked / 60) * 60, credits: null };
  }

  // An edited session can have a gap in it, and its real charge is summed per
  // station — pricing a contiguous block from the start would put minutes on
  // the wrong side of the peak boundary. Don't guess: keep the booked end.
  if (endMin > spanEnd) return asBooked;

  // Counted in thirtieths of a credit rather than in fractions: 10 / 30 is not
  // representable in binary, so the obvious arithmetic makes a 10-minute
  // off-peak plunge "31 minutes paid" off a rounding error of 4e-15. Whole
  // numbers all the way to the answer instead.
  const offMin = Math.max(
    0,
    Math.min(spanEnd, offEnd) - Math.max(startMin, offStart),
  );
  const peakMin = booked - offMin;
  const thirtieths = offMin + 2 * peakMin;
  if (thirtieths <= 0) return asBooked;
  const credits = Math.ceil(thirtieths / MIN_PER_CREDIT_OFF_PEAK);
  const leftover = credits * MIN_PER_CREDIT_OFF_PEAK - thirtieths;
  // The rate the extra time runs on at: the window the booked time ends in.
  const endsOffPeak = spanEnd > offStart && spanEnd <= offEnd;
  const extra = endsOffPeak ? leftover : Math.ceil(leftover / 2);
  return {
    minutes: booked + Math.max(0, extra),
    // A booking that spans both windows isn't "N credits of 30 minutes" in any
    // way worth saying out loud, so it gets the neutral copy instead.
    credits: offMin > 0 && peakMin > 0 ? null : credits,
  };
}

/** Just the minutes the member's credits bought. See paidBlockFor. */
export function paidMinutesFor(v: {
  date?: string | null;
  start: string;
  end: string;
  bookedMinutes: number;
  kind?: "room" | "session";
}): number {
  return paidBlockFor(v).minutes;
}

/**
 * How early the door lets a booked member in, AS TOLD TO MEMBERS. The door
 * itself opens 30 minutes before the start (_zz_door_early_minutes(),
 * 20261029090000_door_opens_30_minutes_early.sql), but that margin exists to
 * spare a member who turns up early a refusal at the door, not to invite
 * everyone 30 minutes early (Ryan, 1 Nov 2026). So the emails and the app
 * keep saying 5 minutes; a test checks the door never opens LATER than this.
 */
export const DOOR_EARLY_MIN = 5;

/** "07:30[:00]" → minutes since midnight, or NaN. */
const hhmmToMin = (t: string): number => {
  const [h, m] = String(t || "")
    .split(":")
    .map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

/**
 * Shift a wall-clock time by some minutes, clamped inside the day — the same
 * clamp the SQL applies (_zz_add_minutes_in_day), because TIME arithmetic
 * wraps and a confirmation must never tell a member they are fine "until
 * 00:10". Returns "" on an unparseable time so callers can drop the line.
 */
export function shiftHhmm(time: string, minutes: number): string {
  const base = hhmmToMin(time);
  if (!Number.isFinite(base)) return "";
  const shifted = base + minutes;
  if (shifted < 0) return "00:00";
  if (shifted > 23 * 60 + 59) return "23:59";
  const h = Math.floor(shifted / 60);
  const m = shifted % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "07:30:00" → "07:30". */
export const hhmmOf = (t: string) => String(t || "").slice(0, 5);

export type VisitWindow = {
  /** booked start, "HH:MM" or "HH:MM:SS" */
  start: string;
  /** booked end of the whole booking (the last station's end) */
  end: string;
  /** minutes actually booked — the sum of the stations, not the span */
  bookedMinutes: number;
  /** a private room is billed differently past the window (see below) */
  kind: "room" | "session";
  /**
   * Booking date, YYYY-MM-DD, read as SAST. Decides which peak window the
   * booking was priced in, and so how much time the credits actually bought.
   * Leave it out and the copy falls back to the booked end — never a promise
   * of time we haven't worked out.
   */
  date?: string | null;
  /**
   * True when an unlimited membership paid for this booking. Machine overstay
   * only bills a subscriber for PEAK minutes, so telling them flatly that
   * extra time costs credits would be over-warning. A private room is charged
   * in full to everyone, membership or not, so this is ignored for rooms.
   */
  coveredByMembership?: boolean;
  /**
   * False when the door doesn't know the member's face yet (no face set up).
   * The note then says "the door lets you in" rather than promising face id.
   * Defaults to true.
   */
  faceReady?: boolean;
};

/**
 * The paid window: the booked end, pushed out by whatever time the member's
 * credits bought and didn't use. An unlimited member paid no credits for an
 * off-peak session, so there is no block to hand back and the booked end
 * stands — the meter is kinder than this, never harsher.
 */
function paidWindowOf(v: VisitWindow): {
  end: string;
  extra: number;
  credits: number | null;
} {
  const end = hhmmOf(v.end);
  if (v.kind === "session" && v.coveredByMembership) {
    return { end, extra: 0, credits: null };
  }
  const block = paidBlockFor(v);
  const extra = Math.max(0, block.minutes - Math.max(0, v.bookedMinutes || 0));
  if (extra <= 0) return { end, extra: 0, credits: null };
  const paidEnd = shiftHhmm(end, extra);
  if (!paidEnd) return { end, extra: 0, credits: null };
  return { end: paidEnd, extra, credits: block.credits };
}

/**
 * How the visit runs, as the timeline it actually is — three windows, each a
 * label and the clock times it covers, in the order they happen:
 *
 *   booked                  08:10–08:26   the time they picked
 *   remaining from credits  08:26–08:40   time their credits bought and the
 *                                         booking didn't use
 *   entry and shower        08:40–08:50   free changing time on top
 *
 * This used to be five prose points ("45 min you've already paid for · yours
 * until 08:40"). A member reads a confirmation at a glance and wants to know
 * one thing: until what time is this mine. Times on the right of a label
 * answer that without being read as a sentence.
 *
 * Every number comes from a rule in SQL: the paid block (_zz_paid_minutes),
 * which is why a 16-minute stack off-peak carries a further 14 minutes the
 * member has already paid for, and the flat 10-minute changing time
 * (_zz_leeway_minutes). What happens after the last window — and the door's
 * advertised 5-minute lead (DOOR_EARLY_MIN; the door itself opens 30 minutes early) — is said once underneath, by
 * visitChargeNote().
 *
 * The middle row is left out when there is nothing spare: an unlimited member
 * off-peak, or a booking that used every minute its credits bought.
 *
 * Duplicated word for word in supabase/functions/_shared/visit-window.ts (a
 * Deno function can't import from src/), and a test compares the two.
 */
export type VisitFact = { k: string; v: string };

export function visitFactsFor(v: VisitWindow): VisitFact[] {
  const start = hhmmOf(v.start);
  const end = hhmmOf(v.end);
  const leeway = leewayMinutesFor();
  const paid = paidWindowOf(v);
  const freeUntil = shiftHhmm(paid.end, leeway);
  // Nothing at all rather than a half-built list: a row with a blank time in
  // it is worse than no row.
  if (!start || !end || !freeUntil) return [];

  const facts: VisitFact[] = [{ k: "booked", v: `${start}–${end}` }];
  // The time their credits already bought and the booking didn't spend. A
  // credit buys 30 minutes off-peak and a 6-minute plunge costs one, so this
  // is usually the biggest window on the card — and the one nobody knew about.
  if (paid.extra > 0) {
    facts.push({ k: "remaining from credits", v: `${end}–${paid.end}` });
  }
  // The changing time, which is free and on top of everything above.
  facts.push({ k: "entry and shower", v: `${paid.end}–${freeUntil}` });
  return facts;
}

/**
 * The line under the timeline: when the door opens, and what staying past the
 * last window costs.
 *
 * Both halves are promises the billing keeps. The door admits a booked member
 * from 5 minutes before their start as far as the member is told (DOOR_EARLY_MIN; the door itself opens 30 minutes early, _zz_door_opens_at), and the overstay meter
 * starts at whichever is kinder — the paid end plus changing time, or the
 * member's own paid minutes plus changing time counted from when they walked
 * in (_zz_meter_start), which is why a late arrival still keeps the time they
 * paid for. A membership covers off-peak on the equipment but never a private
 * room, so a subscriber is warned about peak time only.
 */
export function visitChargeNote(v: VisitWindow): string {
  const start = hhmmOf(v.start);
  const doorOpens = shiftHhmm(start, -DOOR_EARLY_MIN);
  const paid = paidWindowOf(v);
  const freeUntil = shiftHhmm(paid.end, leewayMinutesFor());
  if (!doorOpens || !freeUntil) return "";
  const charge =
    v.kind === "session" && v.coveredByMembership
      ? `any peak time after ${freeUntil} will be charged from your credits`
      : `additional credits will be charged after ${freeUntil}`;
  const opener = v.faceReady === false ? "the door" : "face id";
  return `${opener} lets you in from ${doorOpens} and times you out · ${charge}, counted from when you actually walked in.`;
}

/** One friendly sentence naming the window, for the top of a confirmation. */
export function visitWindowSentence(v: VisitWindow): string {
  const start = hhmmOf(v.start);
  const end = hhmmOf(v.end);
  if (!start || !end) return "";
  const paid = paidWindowOf(v);
  if (paid.extra > 0) {
    // A room can be paid by card, so only a session's cover is "your credits".
    const covers =
      v.kind === "room" || paid.credits === null
        ? "your booking covers you"
        : paid.credits === 1
          ? "your credit covers you"
          : "your credits cover you";
    return `you're booked from ${start} to ${end}, and ${covers} until ${paid.end}.`;
  }
  return `you're booked from ${start} to ${end}.`;
}

export const CANCEL_FULL_HOURS = 48;
export const CANCEL_HALF_HOURS = 12;

export function refundPctFor(hoursUntilStart: number): number {
  if (hoursUntilStart > CANCEL_FULL_HOURS) return 100;
  if (hoursUntilStart > CANCEL_HALF_HOURS) return 50;
  return 0;
}

/**
 * What cancelling THIS booking would give back, said before it is made.
 *
 * The tiers were only ever shown on the terms page and in the cancel dialog —
 * which is after the member has paid and is trying to get out. A same-day
 * booking is inside 12 hours by definition, so it returns nothing, and that is
 * exactly the case a member is most likely to make without realising.
 *
 * One sentence about this one booking, and no further: the schedule it came
 * from is in the policy dialog (cancelPolicyCopy), which this line opens. The
 * fact a member needs before paying is "nothing comes back", not a table.
 *
 * What the short form still has to say out loud is "credits". A refund is paid
 * to the credit balance, never back to the card, whatever the booking was paid
 * with — so a member who put R2,000 on a card for a peak private room and
 * reads "free to cancel" is owed that word before they book, not after they
 * cancel. The dialog carries the rest.
 */
export function cancelTermsFor(
  startISO: string | null | undefined,
  now: Date = new Date(),
): { pct: number; text: string } | null {
  if (!startISO) return null;
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return null;
  const hours = (start.getTime() - now.getTime()) / 3_600_000;
  const pct = refundPctFor(hours);
  // Interpolated, not typed out: a member reading "48 hours" has to be reading
  // the number that actually pays out.
  if (pct === 100)
    return {
      pct,
      text: `free to cancel until ${CANCEL_FULL_HOURS} hours before it starts — the full value comes back as credits, never back to your card.`,
    };
  if (pct === 50)
    return {
      // "and none do later" rather than the hour it happens at: a member on
      // this tier still needs to know it runs out, and the schedule behind it
      // is one tap away in the policy.
      pct,
      text: `cancel now and half the value comes back as credits. leave it later and none does — and refunds are credits, never back to your card.`,
    };
  return {
    pct,
    text: `this starts within ${CANCEL_HALF_HOURS} hours, so cancelling it won't give your credits back. refunds are credits, never back to your card.`,
  };
}

/**
 * The whole cancellation policy, in one place, for the dialog that carries it.
 *
 * It used to be written out wherever it was relevant — under the price on the
 * booking screen, in the private-room "good to know" list, in the cancel
 * confirm, on the terms page — in four different phrasings, and a tier change
 * meant finding all four. Now the detail lives here and is shown in a popup;
 * the places that used to spell it out open that instead.
 *
 * The exceptions, deliberately: the confirm strip a member sees while actually
 * cancelling keeps the tiers on screen (that is the moment the number decides
 * what they do next), the terms page keeps its paragraph because a legal
 * document has to read as text, and the confirmation email keeps its sentence
 * because an inbox has no popups.
 *
 * Numbers interpolated from the constants above, which mirror _zz_refund_pct —
 * a tier that changes in SQL changes this copy with it.
 */
export function cancelPolicyCopy(): {
  title: string;
  intro: string;
  tiers: string[];
  notes: string[];
} {
  return {
    title: "cancellation policy",
    intro: "plans change. here's what comes back if you cancel a booking:",
    tiers: [
      `more than ${CANCEL_FULL_HOURS} hours before it starts — all your credits come back`,
      `${CANCEL_FULL_HOURS} to ${CANCEL_HALF_HOURS} hours before — half come back`,
      `inside ${CANCEL_HALF_HOURS} hours — none come back`,
    ],
    notes: [
      "refunds come back as credits, always whole ones, so a half refund rounds down to the nearest credit.",
      "shortening a booking works the same way — the credits you free up come back on the same three tiers, judged on how much notice you gave.",
      `need a different time instead? a booking can be moved once, to any open time within ${MOVE_WINDOW_HOURS} hours of the original, from your bookings list. it's priced for its new time, and if you cancel later the notice still counts from the original time.`,
      "if the booking cost you nothing — an off-peak visit on your membership — there's nothing to come back.",
      "memberships are separate: a membership is charged once per period and does not renew, so it simply runs to the end of the period you have paid for. that period is not refundable.",
    ],
  };
}

/**
 * How far a booking may be moved from its original start, either way.
 * Mirrors _zz_move_window() in 20261013090000_move_a_booking_once.sql; a test
 * reads the number back out of that migration.
 */
export const MOVE_WINDOW_HOURS = 24;

/**
 * Where a booking may be moved to: from now (or 24 hours before the original
 * start, whichever is later) to 24 hours after it. Null when the booking has
 * no start to speak of, or the whole window is behind us.
 */
export function moveWindowFor(
  startISO: string | null | undefined,
  now: Date = new Date(),
): { from: Date; to: Date } | null {
  if (!startISO) return null;
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return null;
  const span = MOVE_WINDOW_HOURS * 3_600_000;
  const from = new Date(Math.max(now.getTime(), start.getTime() - span));
  const to = new Date(start.getTime() + span);
  if (to.getTime() <= now.getTime()) return null;
  return { from, to };
}

export type MoveRefusal = "room" | "moved" | "started" | "none";

/**
 * Whether the move option applies to a booking, and if not, why — so the
 * cancel dialog can say "already moved once" rather than silently hiding the
 * button. Only a session of real stations can move (a private room is one
 * booking of the whole space), only once, and only before it has started.
 */
export function canMoveBooking(
  b: {
    type?: string;
    movedFrom?: string | null;
    stations?: unknown[];
    startISO?: string;
  },
  now: Date = new Date(),
): { ok: true } | { ok: false; reason: MoveRefusal } {
  if (b.type === "room") return { ok: false, reason: "room" };
  if (!Array.isArray(b.stations) || b.stations.length === 0)
    return { ok: false, reason: "none" };
  if (b.movedFrom) return { ok: false, reason: "moved" };
  const start = b.startISO ? new Date(b.startISO) : null;
  if (!start || Number.isNaN(start.getTime()))
    return { ok: false, reason: "none" };
  if (start.getTime() <= now.getTime()) return { ok: false, reason: "started" };
  return { ok: true };
}

/** "tue 14 oct, 07:30", in the club's time zone. */
function fmtSast(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Johannesburg",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")}, ${get("hour")}:${get("minute")}`.toLowerCase();
}

/**
 * What the cancel dialog says before a member confirms.
 *
 * The confirm used to be one line of tiers in small print. This spells out,
 * for THIS booking: what is being cancelled (a stack goes as one), which tier
 * it is on right now and when that changes, that a refund is credits and never
 * card, and — when it applies — that the booking could be moved instead.
 */
export function cancelConfirmCopy(
  b: {
    what: string;
    when: string;
    steps?: number;
    type?: string;
    startISO?: string;
    movedFrom?: string | null;
    stations?: unknown[];
  },
  now: Date = new Date(),
): {
  title: string;
  lines: string[];
  move: string | null;
  canMove: boolean;
  confirmLabel: string;
} {
  const lines: string[] = [];
  const steps = b.steps || 1;
  lines.push(
    steps > 1
      ? `this cancels your ${b.what} on ${b.when} — all ${steps} steps, since a session is booked and charged as one.`
      : `this cancels your ${b.what} on ${b.when}.`,
  );

  const start = b.startISO ? new Date(b.startISO) : null;
  const valid = !!start && !Number.isNaN(start.getTime());
  const hours = valid ? (start!.getTime() - now.getTime()) / 3_600_000 : 0;
  const pct = valid ? refundPctFor(hours) : 0;
  const at = (hoursBefore: number) =>
    fmtSast(new Date(start!.getTime() - hoursBefore * 3_600_000));
  if (!valid) {
    lines.push(
      `what comes back depends on how much notice you give: everything more than ${CANCEL_FULL_HOURS} hours before, half inside that, nothing inside ${CANCEL_HALF_HOURS} hours.`,
    );
  } else if (pct === 100) {
    lines.push(
      `you're more than ${CANCEL_FULL_HOURS} hours out, so everything you paid comes back. that drops to half from ${at(CANCEL_FULL_HOURS)}, and to nothing from ${at(CANCEL_HALF_HOURS)}.`,
    );
  } else if (pct === 50) {
    lines.push(
      `you're inside ${CANCEL_FULL_HOURS} hours, so half of what you paid comes back, rounded down to whole credits. from ${at(CANCEL_HALF_HOURS)} nothing comes back.`,
    );
  } else {
    lines.push(
      `this starts within ${CANCEL_HALF_HOURS} hours, so nothing you paid comes back.`,
    );
  }
  lines.push(
    "refunds are credits, never back to your card. a visit that cost you nothing on your membership has nothing to come back.",
  );

  const can = canMoveBooking(b, now);
  let move: string | null = null;
  if (can.ok) {
    move = `rather keep it? you can move this booking once, to any open time within ${MOVE_WINDOW_HOURS} hours of the original. same session, priced for its new time — and if you cancel later, the notice still counts from the original time.`;
  } else if (can.ok === false && can.reason === "moved") {
    move =
      "this booking has already been moved once, so it can't be moved again.";
  }

  return {
    title: "cancel this booking?",
    lines,
    move,
    canMove: can.ok,
    confirmLabel: "yes, cancel it",
  };
}

/**
 * A sauna and/or cold plunge always come with a single 3-minute rinse-off
 * shower, positioned **after the sauna and before the cold plunge** — i.e. in a
 * sauna→plunge session the order becomes sauna → shower → plunge. Concretely:
 * the shower is inserted right before the first plunge if there is one,
 * otherwise right after the last sauna. No-op when there's no sauna or plunge,
 * or when a shower is already present. The shower forms part of the booked time,
 * so it's charged with the rest of the session.
 */
export function withRinseShower<T extends { id: string; minutes: number }>(
  steps: T[],
): (T | { id: string; minutes: number })[] {
  if (steps.some((s) => isShowerStep(s.id))) return steps;
  const shower = { id: "shower", minutes: SHOWER_MINUTES };
  const firstPlunge = steps.findIndex((s) => isPlungeStep(s.id));
  if (firstPlunge !== -1) {
    return [
      ...steps.slice(0, firstPlunge),
      shower,
      ...steps.slice(firstPlunge),
    ];
  }
  let lastSauna = -1;
  steps.forEach((s, i) => {
    if (isSaunaStep(s.id)) lastSauna = i;
  });
  if (lastSauna === -1) return steps;
  return [
    ...steps.slice(0, lastSauna + 1),
    shower,
    ...steps.slice(lastSauna + 1),
  ];
}

/** Today / tomorrow in SAST, as YYYY-MM-DD. */
const sastDay = (offsetDays = 0): string =>
  new Date(Date.now() + offsetDays * 86_400_000).toLocaleDateString("en-CA", {
    timeZone: "Africa/Johannesburg",
  });

/**
 * A booked date as members read it: "today", "tomorrow", or
 * "monday 8 september".
 *
 * The stored date is a naive calendar date, so the weekday and month are read
 * off UTC noon in UTC — anchoring it to a zone would shift the day either side
 * of midnight. The words are composed rather than taken from the locale's own
 * pattern, which writes "saturday, 07 november".
 */
export function bookingDayLabel(date: string): string {
  if (date === sastDay(0)) return "today";
  if (date === sastDay(1)) return "tomorrow";
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  const noon = new Date(Date.UTC(y, m - 1, d, 12));
  const part = (opts: Intl.DateTimeFormatOptions) =>
    noon
      .toLocaleDateString("en-ZA", { ...opts, timeZone: "UTC" })
      .toLowerCase();
  return `${part({ weekday: "long" })} ${d} ${part({ month: "long" })}`;
}

/** Normalise an "HH:MM" time to "HH:MM:SS" (leaves already-seconds values). */
export function normaliseTime(t: string): string {
  return t.length === 5 ? t + ":00" : t;
}

export type BookingRpc = {
  fn: "confirm_room_booking" | "confirm_machine_booking";
  args: Record<string, string>;
};

/**
 * Map a booking request to the Supabase RPC + params.
 *  - room  → confirm_room_booking
 *  - session (multi-room) → confirm_machine_booking with the 'circuit' machine
 *  - single → confirm_machine_booking with the mapped machine_id
 */
export function buildBookingRpc(opts: {
  mode: BookingMode;
  room: string | null;
  date: string;
  start: string;
  end: string;
}): BookingRpc {
  const p_start_time = normaliseTime(opts.start);
  const p_end_time = normaliseTime(opts.end);
  if (opts.mode === "room") {
    return {
      fn: "confirm_room_booking",
      args: { p_booking_date: opts.date, p_start_time, p_end_time },
    };
  }
  const p_machine_id =
    opts.mode === "session"
      ? "circuit"
      : MACHINE_MAP[opts.room || ""] || opts.room || "";
  return {
    fn: "confirm_machine_booking",
    args: { p_machine_id, p_booking_date: opts.date, p_start_time, p_end_time },
  };
}

/** "HH:MM[:SS]" → minutes since midnight. */
function toMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}
/** minutes since midnight → "HH:MM:SS". */
function fromMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
}

export type SessionStepWindow = {
  machine_id: string;
  start_time: string;
  end_time: string;
};

/**
 * Lay a multi-machine session out sequentially from `start`: each step runs
 * back-to-back for its own minutes on its own (backend-mapped) machine. Used to
 * book each station individually so per-machine capacity is checked + consumed.
 */
export function buildSessionSteps(opts: {
  start: string;
  steps: { id: string; minutes: number }[];
}): SessionStepWindow[] {
  let cursor = toMinutes(opts.start);
  return opts.steps.map((s) => {
    const startM = cursor;
    cursor += s.minutes;
    return {
      machine_id: MACHINE_MAP[s.id] || s.id,
      start_time: fromMinutes(startM),
      end_time: fromMinutes(cursor),
    };
  });
}

export type DayBooking = {
  start_time: string;
  end_time: string;
  booking_type: string;
  machine_id: string | null;
  /** True when this row is the signed-in member's own booking (get_day_bookings
   *  flags it). Optional so an older/stubbed response just reads as "not mine". */
  mine?: boolean;
};

/**
 * Free capacity for a machine across the window [startMin, startMin+durationMin).
 *
 * Checking the whole window (not just the start instant) is what makes a long
 * booking show as unavailable when it would run into an already-occupied or
 * privately-booked later slot. A durationMin of 0 falls back to a 1-minute
 * window (the start instant). Returns 0 if any 'room' booking overlaps the
 * window (the whole space is taken), or if any of the MEMBER'S OWN bookings
 * overlaps it — nobody can be in two places at once, so the picker greys the
 * slot out rather than letting them choose it and be refused on confirm (the
 * booking RPCs enforce the same rule server-side).
 *
 * 'room' is the private room, which is the whole space: any booking at all in
 * the window — another member's sauna, plunge, anything — means it isn't free.
 * Counting it like a machine only ever matched other room bookings (a machine
 * booking's machine_id is never 'room'), so the picker offered times the
 * server then refused as "already booked".
 *
 * 'plunge' is the pool of both tubs, never a machine_id in its own right: the
 * booking RPCs always store a plunge booking as plunge-1 or plunge-2, so
 * counting it needs both. Without that the pool reads permanently free and the
 * member is only turned away once the server rejects their payment.
 */
export function slotFreeOverWindow(
  rows: DayBooking[],
  machineId: string,
  cap: number,
  startMin: number,
  durationMin: number,
): number {
  const ws = fromMinutes(startMin);
  const we = fromMinutes(startMin + (durationMin > 0 ? durationMin : 1));
  const isPlungePool = machineId === "plunge";
  const isRoom = machineId === "room";
  let occ = 0;
  let roomBlocked = false;
  let alreadyBooked = false;
  for (const b of rows) {
    if (b.start_time < we && b.end_time > ws) {
      if (b.mine) alreadyBooked = true;
      if (b.booking_type === "room" || isRoom) roomBlocked = true;
      else if (
        isPlungePool
          ? b.machine_id === "plunge-1" || b.machine_id === "plunge-2"
          : b.machine_id === machineId
      )
        occ += 1;
    }
  }
  if (roomBlocked || alreadyBooked) return 0;
  return Math.max(0, cap - occ);
}

/** "HH:MM[:SS]" → minutes since midnight (exported for callers/tests). */
export function timeToMinutes(t: string): number {
  return toMinutes(t);
}

/**
 * Private-room cost in credits (1 credit = R100), peak-aware with a 30-minute
 * tier. The 30-min rate is a flat tier, deliberately NOT half the hourly rate;
 * longer bookings round up to whole hours.
 *   off-peak: 30 min = 6 cr (R600),  per hour = 10 cr (R1,000)
 *   peak:     30 min = 12 cr (R1,200), per hour = 20 cr (R2,000)
 * Mirrors the SQL _zz_room_credits and the edge function's roomCredits.
 */
export function roomCredits(window: "peak" | "off", minutes: number): number {
  if (minutes <= 0) return 0;
  const peak = window === "peak";
  if (minutes <= 30) return peak ? 12 : 6;
  return Math.ceil(minutes / 60) * (peak ? 20 : 10);
}

/** Private-room credit cost for a whole number of hours (≥1). */
export function roomCreditCost(window: "peak" | "off", hours: number): number {
  return roomCredits(window, hours * 60);
}

/** Credits a member can apply toward a room booking (capped by balance + cost). */
export function maxApplicableCredits(balance: number, cost: number): number {
  return Math.max(0, Math.min(balance, cost));
}
